import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { colorMaps } from '~/design'
import { mapRangeWithFixedBounds } from '~/prix/utils/math/mapRange'
import { MapContext } from './mapContext'
import { FeatureCollection, Point } from 'geojson'
import serial from '~/prix/utils/serial'
import { WeightedLocation } from '../map/mapPosition'

interface HeatmapProps {
  positions: WeightedLocation[]
  opacity?: number
  radius?: number
  gradient?: string[]
  resolution?: number
}

/**
 * Descobrir e retornar o outlier superior em um array de números.
 * Qualquer valor acima desse outlier é fora da curva.
 * @source https://www.freecodecamp.org/news/what-is-an-outlier-definition-and-how-to-find-outliers-in-statistics/
 * @param dataPoints Array de números
 * @returns Outlier superior do array.
 */
function getOutlierFromArray(dataPoints: number[]) {
  dataPoints.sort((a, b) => a - b)

  const uniqueValues = [...new Set(dataPoints)]

  const lowerQuartileIndex = Math.floor(uniqueValues.length / 4)
  const upperQuartileIndex = Math.floor((uniqueValues.length / 4) * 3)

  const lowerQuartile =
    (uniqueValues[lowerQuartileIndex] + uniqueValues[lowerQuartileIndex + 1]) / 2
  const upperQuartile =
    (uniqueValues[upperQuartileIndex] + uniqueValues[upperQuartileIndex + 1]) / 2

  const interquartilRange = upperQuartile - lowerQuartile

  const upperOutlier = upperQuartile + 1.5 * interquartilRange

  return upperOutlier
}

/**
 * Calcula o radius e maxIntensity do heatmap, para garantir uniformidade e
 * tentar manter uma boa visualização do heatmap.
 * @param zoomLevel Nível de zoom do mapa.
 * @param outlier Outlier superior, qualquer valor acima é fora da curva.
 * @param resolutionThreshold Limite de resolução do heatmap, até qual nível de zoom é possível manter uma boa visualização.
 * @returns [radius, maxIntensity]
 */
function getHeatmapPointsRadiusIntensity(
  zoomLevel: number,
  dataPoints: number[],
  resolutionThreshold: number,
): number[] {
  let intensityThreshold = getOutlierFromArray(dataPoints)
  let coefficient = 3 - zoomLevel / 7

  if (zoomLevel <= 2) {
    coefficient = 3 + zoomLevel
  }

  if (zoomLevel > resolutionThreshold && resolutionThreshold < 10) {
    // Fórmula quadrática obtida através de testes empíricos em diferentes níveis de zoom e resolução
    // tratando casos onde a resolução é maior que o nível de zoom para manter uma melhor visualização.
    const radiusFactor = 0.374 * Math.pow(zoomLevel, 2) - 4.66 * zoomLevel + 21.468
    coefficient = 3 - zoomLevel / radiusFactor
  } else if (zoomLevel > 10 && resolutionThreshold < 12) {
    // Fórmula quadrática obtida através de testes empíricos em diferentes níveis de zoom e resolução
    // tratando casos onde a resolução é maior que o nível de zoom para manter uma melhor visualização.
    const radiusFactor = (0.374 * Math.pow(zoomLevel, 2) - 4.66 * zoomLevel + 21.468) / 2.5
    coefficient = 3 - zoomLevel / radiusFactor
  }

  let radius = Math.max(3, Math.pow(zoomLevel, coefficient) - zoomLevel / 8)
  return [radius, intensityThreshold]
}

const ids = serial()

export default function Heatmap({
  positions,
  opacity,
  radius,
  gradient = colorMaps.toHeatHeatmap,
  resolution = 8,
}: HeatmapProps) {
  const mapContext = useContext(MapContext)
  if (!mapContext) {
    throw new Error('Heatmap must be used within a MapBox map')
  }

  const { map } = mapContext

  const [zoomLevel, setZoomLevel] = useState(4)

  const id = useRef(ids.next())
  const layerId = useMemo(() => `heatmap-layer-${id.current}`, [id.current])
  const sourceId = useMemo(() => `heatmap-data-${id.current}`, [id.current])

  const handleZoom = useCallback(() => {
    if (!map) return
    const currentZoom = Number(map.getZoom().toFixed())
    if (currentZoom !== zoomLevel) {
      setZoomLevel(currentZoom)
    }
  }, [map])

  useEffect(() => {
    if (!map) return

    map.on('zoom', handleZoom)

    return () => {
      map.off('zoom', handleZoom)
    }
  }, [map, handleZoom])

  const heatmapData = useMemo(() => {
    return {
      type: 'FeatureCollection',
      features: positions.map(position => ({
        type: 'Feature',
        properties: {
          weight: position.weight || 1,
        },
        geometry: {
          type: 'Point',
          coordinates: [position.location.lng, position.location.lat],
        },
      })),
    } as FeatureCollection<Point>
  }, [positions])

  useEffect(() => {
    try {
      if (!map || !map.getStyle()) {
        return
      }
    } catch {
      return
    }

    // Calcular raio e intensidade do heatmap baseado na análise interquartil
    let finalRadius: number
    let weightScaleIntensity: number

    if (zoomLevel && resolution !== undefined) {
      const weights = positions.map(p => p.weight || 1)
      const [calculatedRadius, maxIntensity] = getHeatmapPointsRadiusIntensity(
        zoomLevel,
        weights,
        resolution + 2,
      )
      finalRadius = radius ?? calculatedRadius
      weightScaleIntensity = maxIntensity > 0 ? 1 / maxIntensity : 1
    } else {
      const resolutionRadius = mapRangeWithFixedBounds(resolution, 4, 13, 80, 10)
      finalRadius = radius ?? resolutionRadius
      weightScaleIntensity =
        1 /
        (Math.max(...positions.map(p => p.weight || 1)) -
          Math.min(...positions.map(p => p.weight || 1)) +
          1)
    }

    const source = map.getSource(sourceId)
    if (!source) {
      map.addSource(sourceId, {
        type: 'geojson',
        data: heatmapData,
      })
    }

    const layer = map.getLayer(layerId)
    if (layer) {
      map.setPaintProperty(layerId, 'heatmap-intensity', weightScaleIntensity)
      map.setPaintProperty(layerId, 'heatmap-radius', [
        'interpolate',
        ['linear'],
        ['zoom'],
        0,
        finalRadius * 0.1,
        22,
        finalRadius * 3,
      ])
    } else {
      const heatmapColorStops: any[] = []
      const step = 1 / gradient.length

      ;['transparent', ...gradient].forEach((color, index) => {
        heatmapColorStops.push(step * index, color)
      })

      map.addLayer({
        id: layerId,
        type: 'heatmap',
        source: sourceId,
        paint: {
          'heatmap-weight': ['get', 'weight'],
          'heatmap-intensity': weightScaleIntensity,
          'heatmap-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0,
            finalRadius * 0.1,
            22,
            finalRadius * 3,
          ],
          'heatmap-radius-transition': {
            duration: 300,
            delay: 50,
          },
          'heatmap-opacity': opacity ?? 0.8,
          'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'], ...heatmapColorStops],
        },
      })
    }
  }, [map, positions, opacity, radius, gradient, resolution, zoomLevel, layerId, sourceId])

  useEffect(() => {
    if (!map) return
    const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource
    if (source) {
      source.setData(heatmapData)
    }
  }, [map, heatmapData, sourceId])

  useEffect(() => {
    return () => {
      try {
        if (!map || !map.getStyle()) {
          return
        }
      } catch {
        return
      }

      if (map.getLayer(layerId)) {
        map.removeLayer(layerId)
      }
      if (map.getSource(sourceId)) {
        map.removeSource(sourceId)
      }
    }
  }, [map, layerId, sourceId])

  return null
}
